<template>
  <article>
    <MastheadAlt
      title="Create your space on the web with one simple link"
      text="Join the Pros and get your first 30 days free. Cancel anytime."
      :profile-images="[
        '/campsites/creators/campsite-beccajamesuk.png',
        '/campsites/creators/campsite-cassierandolph.png',
        '/campsites/creators/campsite-chrisbethea.png',
        '/campsites/creators/campsite-copycompass.png',
        '/campsites/creators/campsite-dothedamnthing.png',
        '/campsites/creators/campsite-gisellemays.png',
        '/campsites/creators/campsite-jamiebareno.png',
        '/campsites/creators/campsite-heyjessica.png',
        '/campsites/creators/campsite-organicallybecca.png',
        '/campsites/creators/campsite-thewinestylings.png',
        '/campsites/creators/campsite-artisiq.png',
        '/campsites/creators/campsite-alexokoroji.png',
        '/campsites/creators/campsite-mattevans.png',
        '/campsites/creators/campsite-adionnewilliams.png',
      ]"
    >
      <Button
        href="https://app.campsite.bio/create-account?freeTrial=true&amp;freeTrialEntryPoint=PAID_MEDIA"
        size="large"
        >Start my 30-day free trial</Button
      >
    </MastheadAlt>

    <SeenOn />

    <FeatureSection title="You never have to change the link in your bio again">
      <VText slot="text"
        >In 2017 Campsite.bio started helping solve a uniquely annoying problem:
        only having a single link in your bio. Today, Campsite.bio is much more
        than that, allowing businesses and creators to get more from their
        audience. Grow your following, take payments right on your bio, get
        detailed insights and analytics, and control how your content is
        discovered.</VText
      >

      <FeatureCard
        span="2"
        title="Create a custom mini-website in minutes"
        bg-color="secondary"
        color="secondary"
      >
        <SvgIcon viewBox="0 0 24 24" slot="icon">
          <path
            d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A6,6 0 0,1 16,17H14.2C13.9,17 13.7,17.2 13.7,17.5C13.7,17.6 13.8,17.7 13.8,17.8C14.2,18.3 14.4,18.9 14.4,19.5C14.5,20.9 13.4,22 12,22M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C12.3,20 12.5,19.8 12.5,19.5C12.5,19.3 12.4,19.2 12.4,19.1C12,18.6 11.8,18.1 11.8,17.5C11.8,16.1 12.9,15 14.3,15H16A4,4 0 0,0 20,11C20,7.1 16.4,4 12,4M6.5,10C7.3,10 8,10.7 8,11.5C8,12.3 7.3,13 6.5,13C5.7,13 5,12.3 5,11.5C5,10.7 5.7,10 6.5,10M9.5,6C10.3,6 11,6.7 11,7.5C11,8.3 10.3,9 9.5,9C8.7,9 8,8.3 8,7.5C8,6.7 8.7,6 9.5,6M14.5,6C15.3,6 16,6.7 16,7.5C16,8.3 15.3,9 14.5,9C13.7,9 13,8.3 13,7.5C13,6.7 13.7,6 14.5,6M17.5,10C18.3,10 19,10.7 19,11.5C19,12.3 18.3,13 17.5,13C16.7,13 16,12.3 16,11.5C16,10.7 16.7,10 17.5,10Z"
          />
        </SvgIcon>
        <VText
          >Showing off your personality and brand is important. Campsite.bio
          gives you the customization tools to make your bio link stand out and
          look professional.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/creators/appearance-page.png"
          loading="lazy"
          alt="Appearance page"
        />
      </FeatureCard>
      <FeatureCard title="Automation on your link in bio">
        <SettingsIcon slot="icon" />
        <VText
          >Keep your profile fresh with our automated link types, like the feed
          and image grid. They'll pull in the newest content from Youtube, a
          blog, or Instagram, automatically.
        </VText>
      </FeatureCard>
      <FeatureCard title="Premium link types">
        <GridIcon slot="icon" />
        <VText
          >Unlock our best link types with multiple carousels, opt-in forms,
          feed links, forms (Typeform and Jotform), image grids and so much
          more.</VText
        >
      </FeatureCard>
      <FeatureCard title="Grow your followers across all your platforms">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Give your followers a single place to find all of your content.
          Everything you do online, in one place.
        </VText>
      </FeatureCard>
      <FeatureCard title="Get insights into your users with in-depth analytics">
        <LineChartIcon slot="icon" />
        <VText
          >Our powerful analytics dashboard gives you insights into your users
          and what they are engaging with.
        </VText>
      </FeatureCard>
      <FeatureCard title="Multiple profiles in one place">
        <AccountGroupIcon slot="icon" />
        <VText
          >Do you have multiple brands or businesses? Manage them all from a
          single login for an small additional price. No need to create another
          account or pay for an additional subscription.
        </VText>
      </FeatureCard>
      <FeatureCard title="Custom domain for your profile">
        <WebsiteIcon slot="icon" />
        <VText
          >Show your audience a professional link in bio page with your own URL,
          no Campsite.bio to be found (ex. links.mydomain.com).
        </VText>
      </FeatureCard>
    </FeatureSection>

    <div class="standalone-btn grid grid--container text--center">
      <Button
        href="https://app.campsite.bio/create-account?freeTrial=true&amp;freeTrialEntryPoint=PAID_MEDIA"
        variant="hard-shadow"
        size="large"
        >Start my 30-day free trial</Button
      >
    </div>

    <TestimonialSection
      title="Campsite.bio has helped my business and my audience grow"
      img-src="/campsites/creators/campsite-choicehacking.png"
      name="Jennifer Clinehens"
    >
      <p>
        Having a professional link in bio page is really important to my
        business since most of my traffic comes from different social channels
        like Instagram, Twitter, and LinkedIn - Campsite's features have helped
        me look professional and their embed features have increased my email
        opt-ins, product sales, and YouTube and podcast downloads.
      </p>
      <p>
        I can't recommend Campsite enough if you're looking for a professional
        link in bio to grow your business.
      </p>
    </TestimonialSection>

    <div class="how-it-works">
      <div class="grid grid--container">
        <VText component="h2" variant="h1" style="margin-bottom: 0.5rem"
          >How it works</VText
        >
        <VText gutter-bottom style="font-size: 1.25rem"
          >Three quick steps to connect your followers to your content, with a
          single link.</VText
        >
        <Button
          href="https://app.campsite.bio/create-account?freeTrial=true&amp;freeTrialEntryPoint=PAID_MEDIA"
          variant="hard-shadow"
          >Start my 30-day free trial</Button
        >

        <div class="how-it-works__grid">
          <FeatureCard title="1. Create your free Pro account">
            <PencilIcon slot="icon" />
            <VText
              >It takes less than 60 seconds to create and set up your account.
              Get access to all of our Pro features for 30 days. You can cancel
              at any time or switch to a free account.
            </VText>
          </FeatureCard>
          <FeatureCard title="2. Make your custom profile">
            <SvgIcon viewBox="0 0 24 24" slot="icon">
              <path
                d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A6,6 0 0,1 16,17H14.2C13.9,17 13.7,17.2 13.7,17.5C13.7,17.6 13.8,17.7 13.8,17.8C14.2,18.3 14.4,18.9 14.4,19.5C14.5,20.9 13.4,22 12,22M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C12.3,20 12.5,19.8 12.5,19.5C12.5,19.3 12.4,19.2 12.4,19.1C12,18.6 11.8,18.1 11.8,17.5C11.8,16.1 12.9,15 14.3,15H16A4,4 0 0,0 20,11C20,7.1 16.4,4 12,4M6.5,10C7.3,10 8,10.7 8,11.5C8,12.3 7.3,13 6.5,13C5.7,13 5,12.3 5,11.5C5,10.7 5.7,10 6.5,10M9.5,6C10.3,6 11,6.7 11,7.5C11,8.3 10.3,9 9.5,9C8.7,9 8,8.3 8,7.5C8,6.7 8.7,6 9.5,6M14.5,6C15.3,6 16,6.7 16,7.5C16,8.3 15.3,9 14.5,9C13.7,9 13,8.3 13,7.5C13,6.7 13.7,6 14.5,6M17.5,10C18.3,10 19,10.7 19,11.5C19,12.3 18.3,13 17.5,13C16.7,13 16,12.3 16,11.5C16,10.7 16.7,10 17.5,10Z"
              />
            </SvgIcon>
            <VText
              >Customize your profile with your own colors, fonts, and images to
              create a unique on brand experience for your followers. Link to
              your social media, videos, blog, products, and more.
            </VText>
          </FeatureCard>
          <FeatureCard title="3. Connect &amp; grow your following">
            <AccountMultipleIcon slot="icon" />
            <VText
              >Add your Campsite.bio profile URL to your social media bios, in
              your email signature, or use a QR code to bring the offline world
              online. Your followers will be able to find all of your content in
              one place.
            </VText>
          </FeatureCard>
        </div>
      </div>
    </div>

    <section class="pricing__faq">
      <div class="grid grid--container">
        <VText variant="h2" color="white" gutter-bottom
          >You've got questions? We've got answers.</VText
        >

        <AccordionItemGroup>
          <AccordionItem title="What is Campsite.bio?">
            <p>
              Campsite.bio is a link in bio solution. It allows you to
              consolidate your web presence in an easy to use and share link.
              You can share this personalized link on any social media platform,
              in your email signature, or even on a business card. It's the only
              link you'll ever need to share.
            </p>
          </AccordionItem>
          <AccordionItem title="Why do I need a Campsite.bio?">
            <p>
              Campsite.bio is a single link that lets you connect followers to
              all of your content, everywhere. It's the only link you'll ever
              need to share. It enables you to share many links in a single,
              consolidated landing page. It makes it easy for your followers and
              customers to find you and your content.
            </p>

            <p>
              You can add your bio link on social media like Instagram or
              TikTok, include it on your email signature, or share it via a QR
              code.
            </p>
          </AccordionItem>
          <AccordionItem title="What if I need more profiles/accounts?">
            <p>
              Support for multiple profiles is built right into our product. We
              make it easy to manage them from a single account with a single
              login. The Pro plan supports one profile, with the ability to add
              more for $1.50/month each.
            </p>
          </AccordionItem>
          <AccordionItem title="Do I need a website to use Campsite.bio?">
            <p>
              No, you don't need a website to use Campsite.bio. A Campsite.bio
              profile can act as a mini-website for your business. You can add
              your logo, brand colors, and images to create a unique experience
              for your followers. You don't need any special skills to create
              and customize your link in bio.
            </p>
          </AccordionItem>
        </AccordionItemGroup>
      </div>
    </section>

    <RaisedCtaSection
      title="Start using Campsite.bio today"
      text="Simply click the button to create your account."
    >
      <Button
        href="https://app.campsite.bio/create-account?freeTrial=true&amp;freeTrialEntryPoint=PAID_MEDIA"
        size="large"
        color="secondary"
        variant="hard-shadow"
        >Start my 30-day free trial</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Button,
  SvgIcon,
  WebsiteIcon,
  AccountMultipleIcon,
  GridIcon,
  SettingsIcon,
  PencilIcon,
  AccordionItem,
  AccordionItemGroup,
  LineChartIcon,
  AccountGroupIcon,
} from '@campsite-bio/component-lib';
import { useMediaQuery } from '@vueuse/core';

import SeenOn from '@/components/seen-on-analytics';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import { generateMeta } from '~/utils/generate-meta';
import MastheadAlt from '@/components/sections/masthead-alt';
import TestimonialSection from '@/components/testimonials/testimonial-section';

const imgSrc = require('~/assets/images/users/choice-hacking.png');

export default {
  components: {
    VText,
    Button,
    SeenOn,
    RaisedCtaSection,
    SvgIcon,
    WebsiteIcon,
    AccountMultipleIcon,
    AccountGroupIcon,
    PencilIcon,
    GridIcon,
    SettingsIcon,
    LineChartIcon,
    FeatureCard,
    FeatureSection,
    MastheadAlt,
    AccordionItem,
    AccordionItemGroup,
    TestimonialSection,
  },

  head() {
    return generateMeta({
      title: 'Join Campsite.bio Pro For Free',
      description:
        'Campsite.bio is a social media management tool that connects all of your social profiles with one easy link. Start your 30-day free trial today!',
    });
  },

  setup() {
    const isLargerScreen = useMediaQuery('(min-width: 768px)');

    return {
      isLargerScreen,
      imgSrc,
    };
  },
};
</script>

<style lang="scss" scoped>
.standalone-btn {
  padding-bottom: 3rem;
}

.how-it-works {
  background-color: var(--g-color-light-blue);
  text-align: center;
  padding: 4rem 0;

  @media (min-width: 768px) {
    padding: 6rem 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 2rem;
    margin-top: 3.5rem;
  }
}

.pricing__faq {
  background-color: var(--g-color-dark-green);
  color: white;
  padding: 4rem 0 5rem;
  text-align: center;

  .grid {
    max-width: 800px;
  }
}
</style>
